import { sirenControllerSearchSirenQueryOptions, type Company } from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import _ from 'lodash';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyInformation } from '../company-information/company-information';
import { SearchInput } from '../search-input';
import { useQuery } from '@tanstack/react-query';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type CompanySearchInputProps = {
  onCompanySelect?: (company: Company) => void;
  defaultValue?: Company;
  value?: Company;
  className?: string;
  /**
   * If true, the company information will be displayed below the search input.
   */
  displayCompanyInformation?: boolean;
};

export const CompanySearchInput = forwardRef<HTMLInputElement, CompanySearchInputProps>(
  ({ onCompanySelect, defaultValue, value, className, displayCompanyInformation = false }, ref) => {
    const { t } = useTranslation();
    // When prefilled with a SIREN but no SIRET, search by SIREN to resolve the full company.
    const needsResolution = !!value?.name && !value?.siret;
    const searchQuery = needsResolution ? (value?.siren ?? value?.name ?? '') : (value?.name ?? '');

    const [search, setSearch] = useState(searchQuery);

    const debouncedValue = useDebouncedValue(search, 500, {
      leading: true,
    });

    let searchParams: { siren: string } | { siret: string } | { name: string };
    if (debouncedValue?.length === 9 && !Number.isNaN(Number(debouncedValue))) {
      searchParams = { siren: debouncedValue };
    } else if (debouncedValue?.length === 14 && !Number.isNaN(Number(debouncedValue))) {
      searchParams = { siret: debouncedValue };
    } else {
      searchParams = { name: debouncedValue };
    }

    // Quotes and backslashes are stripped by the backend before querying the
    // Sirene registry — a name made only of them is rejected with a 400.
    const hasNoSearchableCharacters =
      'name' in searchParams &&
      !!searchParams.name &&
      searchParams.name.replace(/["\\]/g, '').trim() === '';

    const isEmptySearch = 'name' in searchParams && searchParams.name === '';

    const { data, isLoading } = useQuery({
      ...sirenControllerSearchSirenQueryOptions(searchParams),
      enabled: !hasNoSearchableCharacters && !isEmptySearch,
    });
    const [selected, setSelected] = useState<Company | null>(defaultValue ?? null);
    const hasAutoResolved = useRef(false);

    useEffect(() => {
      if (needsResolution) {
        setSearch(value?.siren ?? value?.name ?? '');
        hasAutoResolved.current = false;
      }
    }, [needsResolution, value?.siren, value?.name]);

    // When prefilled without SIRET, auto-select the first search result to resolve full company data.
    useEffect(() => {
      if (!needsResolution || hasAutoResolved.current) return;
      if (!data?.data?.length || isLoading) return;
      hasAutoResolved.current = true;
      const match = data.data[0];
      setSelected(match);
      onCompanySelect?.(match);
    }, [needsResolution, data, isLoading, onCompanySelect]);

    const resolvedValue = needsResolution && selected?.siret ? selected : value;

    return (
      <>
        <SearchInput
          className={className}
          ref={ref}
          selected={
            _.isEmpty(_.omitBy(resolvedValue ?? selected, _.isNil))
              ? null
              : (resolvedValue ?? selected)
          }
          itemKey="siret"
          search={search}
          setSearch={setSearch}
          isLoading={isLoading}
          placeholder={t('companySearchInput.placeholder')}
          errorMessage={
            hasNoSearchableCharacters ? t('companySearchInput.noSearchableCharacters') : undefined
          }
          items={hasNoSearchableCharacters ? [] : data?.data || []}
          renderItem={company => {
            return company.name ? (
              <Group align="stretch" justify="between" gap={0} className="w-full">
                <div className="font-medium">
                  {company.name}
                  {company.location?.postCode && (
                    <Text inline tone="muted">
                      {' '}
                      ({company.location.postCode})
                    </Text>
                  )}
                  {_.get(company, 'isActive') === false && (
                    <Text
                      inline
                      size="xs"
                      weight="semibold"
                      className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-red-700"
                    >
                      {t('companySearchInput.closed')}
                    </Text>
                  )}
                </div>
                <div className="ml-1 overflow-hidden text-ellipsis text-end">
                  {company.nafLabel}
                </div>
              </Group>
            ) : null;
          }}
          onSelect={item => {
            setSelected(item);
            onCompanySelect?.(item);
          }}
        />
        {displayCompanyInformation && <CompanyInformation siret={resolvedValue?.siret} />}
      </>
    );
  },
);
